import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, MoreHorizontal } from "lucide-react";
import Icon from "../../../../shared/components/Icon";
import CreateNewDropdown from "./CreateNewDropdown";
import { cryptiqueAnalyticsTheme as theme } from "../theme";

export default function AnalyticsTopBar({
  breadcrumbs = [],
  showCreateNew = true,
  showTimeRange = true,
  onSave,
  onClose,
  showSaveClose = false,
  saveOnly = false,
  saveStatus,
  reorderSaving = false,
  saveDisabled = false,
  showBoardActions = false,
  onShare,
  onDelete,
  onRename,
}) {
  const { team } = useParams();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const createAnchorRef = useRef(null);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef(null);

  useEffect(() => {
    if (!moreMenuOpen) return;
    const handleClickOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setMoreMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [moreMenuOpen]);

  return (
    <header
      className="sticky top-0 z-40 flex items-center justify-between gap-4 px-4 py-3 border-b"
      style={{
        fontFamily: "Raleway, sans-serif",
        backgroundColor: "#ffffff",
        borderColor: theme.border,
      }}
    >
      <div className="flex items-center gap-4 min-w-0">
        <nav className="text-sm flex items-center gap-2 truncate" style={{ color: theme.textMuted }}>
          {breadcrumbs.length > 0 ? (
            <>
              {breadcrumbs.map((crumb, i) => {
                const label = typeof crumb === "object" && crumb !== null ? crumb.label : crumb;
                const path = typeof crumb === "object" && crumb !== null ? crumb.path : undefined;
                const isClickable = path != null || (typeof crumb === "string" && i === 0);
                const href = path != null ? path : (i === 0 ? `/${team}/analytics/reports` : null);
                return (
                  <span key={i}>
                    {i > 0 && <span className="mx-1" style={{ color: theme.border }}>/</span>}
                    {isClickable && href ? (
                      <button
                        type="button"
                        onClick={() => navigate(href)}
                        className="transition-colors hover:underline"
                        style={{ color: theme.textSecondary }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = theme.primary; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = theme.textSecondary; }}
                      >
                        {label}
                      </button>
                    ) : (
                      <span style={{ color: theme.textSecondary }}>{label}</span>
                    )}
                  </span>
                );
              })}
            </>
          ) : null}
          {breadcrumbs.length > 0 && saveStatus && !saveOnly && (
            <span
              className="text-xs font-medium"
              style={{
                color: saveStatus === "saving" ? theme.textMuted : saveStatus === "saved" ? theme.accentPositive : theme.textMuted,
              }}
            >
              {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved" : ""}
            </span>
          )}
          {breadcrumbs.length === 0 && (
            <button
              type="button"
              onClick={() => navigate(`/${team}/analytics/reports`)}
              className="transition-colors"
              style={{ color: theme.textSecondary }}
              onMouseEnter={(e) => { e.currentTarget.style.color = theme.primary; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = theme.textSecondary; }}
            >
              Boards
            </button>
          )}
        </nav>

        {showCreateNew && (
          <div className="relative" ref={createAnchorRef}>
            <button
              type="button"
              onClick={() => setCreateOpen((o) => !o)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors"
              style={{ color: "#fff", backgroundColor: theme.primaryLight }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.primaryHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = theme.primaryLight;
              }}
            >
              <span className="text-lg leading-none">+</span>
              Create New
              <ChevronDown className="w-4 h-4" />
            </button>
            <CreateNewDropdown
              open={createOpen}
              onClose={() => setCreateOpen(false)}
              anchorRef={createAnchorRef}
            />
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {showBoardActions && (
          <>
            {/* Share link */}
            <button
              type="button"
              onClick={() => onShare?.()}
              className="flex items-center gap-1.5 px-2 py-1.5 text-sm transition-colors rounded"
              style={{ color: theme.textSecondary }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = theme.primary;
                e.currentTarget.style.backgroundColor = theme.bgOverlay;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = theme.textSecondary;
                e.currentTarget.style.backgroundColor = "transparent";
              }}
              title="Share"
            >
              <span className="font-medium">Share</span>
              <Icon name="share" size={16} />
            </button>
            {/* Three-dot menu: Delete, Rename */}
            <div className="relative" ref={moreMenuRef}>
              <button
                type="button"
                onClick={() => setMoreMenuOpen((o) => !o)}
                className="flex items-center justify-center w-8 h-8 rounded transition-colors"
                style={{
                  color: theme.textMuted,
                  backgroundColor: moreMenuOpen ? theme.bgOverlay : "transparent",
                }}
                onMouseEnter={(e) => {
                  if (!moreMenuOpen) e.currentTarget.style.backgroundColor = theme.bgOverlay;
                  e.currentTarget.style.color = theme.textPrimary;
                }}
                onMouseLeave={(e) => {
                  if (!moreMenuOpen) e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = theme.textMuted;
                }}
                title="More options"
                aria-haspopup="true"
                aria-expanded={moreMenuOpen}
              >
                <MoreHorizontal className="w-5 h-5" />
              </button>
              {moreMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-1 py-1 rounded-lg shadow-lg border z-50 min-w-[160px]"
                  style={{
                    backgroundColor: "#ffffff",
                    borderColor: theme.border,
                  }}
                  role="menu"
                >
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors rounded-none first:rounded-t-lg last:rounded-b-lg"
                    style={{ color: theme.textPrimary }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme.bgOverlay; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                    onClick={() => { setMoreMenuOpen(false); onDelete?.(); }}
                  >
                    <Icon name="delete" size={16} color={theme.textMuted} />
                    Delete
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors rounded-none first:rounded-t-lg last:rounded-b-lg"
                    style={{ color: theme.textPrimary }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme.bgOverlay; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                    onClick={() => { setMoreMenuOpen(false); onRename?.(); }}
                  >
                    <Icon name="edit_pencil" size={18} color={theme.textMuted} />
                    Rename
                  </button>
                </div>
              )}
            </div>
          </>
        )}
        {showSaveClose && (saveOnly ? (
          <>
            {(saveStatus || reorderSaving) && (
              <span
                className="text-xs font-medium"
                style={{
                  color: saveStatus === "saving" || reorderSaving ? theme.textMuted : saveStatus === "saved" ? theme.accentPositive : theme.textMuted,
                }}
              >
                {saveStatus === "saving" || reorderSaving ? "Saving…" : saveStatus === "saved" ? "Saved" : ""}
              </span>
            )}
            <button
              type="button"
              onClick={onSave}
              disabled={saveStatus === "saving" || reorderSaving || saveDisabled}
              className="px-4 py-1.5 text-sm font-medium transition-colors rounded-lg disabled:opacity-60"
              style={{ color: "#fff", backgroundColor: "#4B57AA" }}
              onMouseEnter={(e) => {
                if (saveStatus !== "saving" && !reorderSaving && !saveDisabled) e.currentTarget.style.backgroundColor = "#3d4699";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#4B57AA";
              }}
            >
              Save
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="p-2 transition-colors"
              style={{ color: theme.textMuted }}
              onMouseEnter={(e) => { e.currentTarget.style.color = theme.textPrimary; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = theme.textMuted; }}
              title="Share"
            >
              <Icon name="share" size={16} />
            </button>
            <button
              type="button"
              className="p-2 transition-colors"
              style={{ color: theme.textMuted }}
              onMouseEnter={(e) => { e.currentTarget.style.color = theme.textPrimary; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = theme.textMuted; }}
              title="More"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm transition-colors"
              style={{ color: theme.textSecondary }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.bgOverlay;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Close
            </button>
            <button
              type="button"
              onClick={onSave}
              className="px-4 py-1.5 text-sm font-medium transition-colors rounded-lg"
              style={{ color: "#fff", backgroundColor: "#4B57AA" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#3d4699";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#4B57AA";
              }}
            >
              Save
            </button>
          </>
        ))}
      </div>
    </header>
  );
}
